import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { addToWaitlist } from '../services/waitlistService';
import analyticsService from '../services/analyticsService';
import logo from '../images/new_logo.png';

const Home = () => {
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    setError('');

    try {
      await addToWaitlist(email);
      setIsSubmitted(true);
      setEmail(''); // Clear the input
      
      // Track waitlist signup
      analyticsService.trackWaitlistSignup(email);
    } catch (error) {
      setError(error.message || 'Failed to join waitlist. Please try again.');
      console.error('Waitlist submission error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-logo">
            <img src={logo} alt="BullBearly Logo" className="hero-logo-img" />
            <div className="hero-title">
              <span className="bull-text">BULL</span>
              <span className="bearly-text">BEARLY</span>
            </div>
          </div>
          
          <h1 className="hero-headline">
            The Future of Financial Intelligence
          </h1>
          
          <p className="hero-description">
            BullBearly revolutionizes stock market analysis by combining AI-powered company detection, 
            real-time financial data, and intelligent news aggregation. Get comprehensive insights 
            from multiple sources including Bloomberg, CNBC, Yahoo Finance, and more.
          </p>

          {user && (
            <div className="authenticated-actions">
              <Link to="/news-feeds" className="cta-button primary">
                📈 View News Feeds
              </Link>
              <Link to="/settings" className="cta-button secondary">
                ⚙️ Configure Settings
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* App Preview Section */}
      <section className="preview-section">
        <div className="preview-container">
          <h2 className="section-title">See BullBearly in Action</h2>
          <p className="section-subtitle">
            Experience our powerful news aggregation and AI analysis platform
          </p>
          
          <div className="preview-mockup">
            <div className="mockup-content">
              <div className="mockup-title">LATEST FINANCIAL NEWS</div>
              
              <div className="mockup-filters">
                <div className="filter-group">
                  <span>Filter by source:</span>
                  <div className="mockup-dropdown">All Sources</div>
                </div>
                <div className="filter-group">
                  <span>Articles to load:</span>
                  <div className="mockup-dropdown">10</div>
                </div>
                <div className="filter-group">
                  <input type="checkbox" checked readOnly />
                  <span>Auto-refresh</span>
                </div>
                <div className="mockup-refresh-btn">REFRESH NOW</div>
              </div>
              
              <div className="mockup-news-container">
                <div className="mockup-news-item">
                  <div className="news-content">
                    <h3 className="news-headline">A Methane Leak Pushed $19 Billion Santos Deal Over the Edge - Bloomberg.com</h3>
                    <div className="news-meta">
                      <span className="news-date">Sep 18, 08:12 PM</span>
                      <div className="mockup-btn">READ MORE →</div>
                      <div className="source-tag">BLOOMBERG</div>
                    </div>
                    
                    <div className="ai-analysis-demo">
                      <div className="ai-analysis-header">
                        <div className="ai-icon">🤖</div>
                        <span>Demo AI "risk" analysis</span>
                      </div>
                      <div className="ai-analysis-content">
                        <p className="ai-insight">
                          <strong>Risk Assessment:</strong> Santos Ltd. (STO) faces heightened exposure to commodity price volatility and operational risks in offshore projects. Regulatory shifts in Australia's energy transition policies could delay project approvals and pressure margins. Potential short-term volatility expected in oil & gas sector equities.
                        </p>
                        <div className="ai-tags">
                          <span className="ai-tag risk">HIGH RISK</span>
                          <span className="ai-tag sector">ENERGY</span>
                          <span className="ai-tag impact">REGULATORY</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="stock-card">
                    <div className="stock-header">
                      <div className="stock-ticker">STO</div>
                      <div className="stock-name">Santos Ltd</div>
                      <div className="stock-price">$4.56</div>
                      <div className="stock-change negative">-1.23%</div>
                    </div>
                    <div className="stock-metrics">
                      <div className="metric">MARKET CAP: $12.34B</div>
                      <div className="metric">P/E: 15.7</div>
                      <div className="metric">SECTOR: Energy</div>
                      <div className="metric">
                        RATING: <span className="rating-buy">BUY</span>
                      </div>
                      <div className="metric">DIVIDEND: 2.34%</div>
                      <div className="metric">TARGET: $5.89</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Early Bird Pricing Section */}
      <section className="pricing-section">
        <div className="pricing-container">
          <h2 className="section-title">Early Bird Pricing</h2>
          <p className="section-subtitle">Limited time offers for early adopters</p>
          
          <div className="pricing-cards">
            <div className="pricing-card featured">
              <div className="pricing-badge">MOST POPULAR</div>
              <h3>Early Bird - BYOK</h3>
              <div className="price">
                <span className="currency">$</span>
                <span className="amount">10</span>
                <span className="period">/month</span>
              </div>
              <p className="price-description">Bring Your Own Key</p>
              <ul className="features-list">
                <li>✅ Unlimited news feeds</li>
                <li>✅ AI-powered company detection</li>
                <li>✅ Custom prompts & analysis</li>
                <li>✅ Real-time stock data</li>
                <li>✅ Export capabilities</li>
                <li>🔑 Use your own OpenAI API key</li>
              </ul>
                     <button className="pricing-button" disabled>Coming Soon</button>
            </div>
            
            <div className="pricing-card">
              <h3>Early Bird - Managed</h3>
              <div className="price">
                <span className="currency">$</span>
                <span className="amount">19</span>
                <span className="period">/month</span>
              </div>
              <p className="price-description">We handle the API keys</p>
              <ul className="features-list">
                <li>✅ Everything in BYOK plan</li>
                <li>✅ Managed OpenAI API access</li>
                <li>✅ Priority support</li>
                <li>✅ Advanced analytics</li>
                <li>✅ Custom integrations</li>
                <li>🛡️ Managed security</li>
              </ul>
                     <button className="pricing-button" disabled>Coming Soon</button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="features-container">
          <h2 className="section-title">Why Choose BullBearly?</h2>
          
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🤖</div>
              <h3>AI-Powered Detection</h3>
              <p>Advanced machine learning algorithms automatically detect companies in news headlines and extract comprehensive financial data.</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">📊</div>
              <h3>Multi-Source Aggregation</h3>
              <p>Combine data from Bloomberg, CNBC, Yahoo Finance, Fox Business, and more in one unified dashboard.</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">⚡</div>
              <h3>Real-Time Updates</h3>
              <p>Get instant notifications and updates as market conditions change and new information becomes available.</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">🎯</div>
              <h3>Custom Analysis</h3>
              <p>Create custom prompts and analysis templates to get insights tailored to your investment strategy.</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">📱</div>
              <h3>Responsive Design</h3>
              <p>Access your data anywhere with our mobile-friendly interface that works on all devices.</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">🔒</div>
              <h3>Secure & Private</h3>
              <p>Your data is encrypted and secure. Choose to use your own API keys or our managed service.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mailing List Section */}
      <section className="mailing-section">
        <div className="mailing-container">
          <h2>Get Early Access</h2>
          <p>Be the first to know when BullBearly launches with exclusive early bird pricing!</p>
          
                 {!isSubmitted ? (
                   <form className="mailing-form" onSubmit={handleSubmit}>
                     <div className="form-group centered">
                       <input
                         type="email"
                         placeholder="Enter your email address"
                         value={email}
                         onChange={(e) => setEmail(e.target.value)}
                         required
                         className="mailing-input"
                         disabled={isLoading}
                       />
                       <button 
                         type="submit" 
                         className="mailing-button"
                         disabled={isLoading}
                       >
                         {isLoading ? 'Joining...' : 'Join Waitlist'}
                       </button>
                       {error && (
                         <div className="error-message">
                           {error}
                         </div>
                       )}
                     </div>
                   </form>
                 ) : (
            <div className="success-message">
              <h3>🎉 Thank you for joining our waitlist!</h3>
              <p>We'll notify you as soon as BullBearly is ready for launch.</p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-brand">
              <img src={logo} alt="BullBearly" className="footer-logo" />
              <span className="footer-brand-text">BullBearly</span>
            </div>
            <div className="footer-links">
              <Link to="/how-it-works" className="footer-link">How It Works</Link>
              <Link to="/terms" className="footer-link">Terms of Service</Link>
              <Link to="/privacy" className="footer-link">Privacy Policy</Link>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 BullBearly, LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
